import { useEffect } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { mostrarNotif } from "./useNotificacoes";
import { toast } from "./useToast";
import { fbCollectionPath } from "./useFirebase";
import { fmtMoeda } from "../utils/format";
import { pedidoOnlineOculto } from "../constants/statusPedido";

const isAndroidApp = () => window.Capacitor?.getPlatform?.() === "android";
const LIMIAR_BAIXO = 0.2;

// Notificação em tempo real pra pedido novo no site e filamento acabando —
// só web/Electron (o desktop agora fica sempre rodando na bandeja). No
// Android essas mesmas notificações já chegam via push (FCM), então evita
// duplicar aqui. Mensagem de chat de um pedido fica só por push/e-mail (evita
// precisar de uma consulta entre lojas de outros vendedores); já a conversa
// livre da vitrine (conversas_loja) mora numa coleção só do vendedor e é
// escutada aqui direto.
export function useNotificacoesEventos(uid) {
  useEffect(() => {
    if (!uid || isAndroidApp()) return;

    // Pedido novo no site — ignora o snapshot inicial (pedidos já existentes).
    // Checkout do Mercado Pago só conta quando o servidor confirma o pagamento
    // (sai de "aguardando_pagamento" para "pago").
    let primeiraPedidos = true;
    const ocultos = new Set();
    const unsubPedidos = onSnapshot(collection(db, `users/${uid}/pedidos_online`), (snap) => {
      if (primeiraPedidos) {
        primeiraPedidos = false;
        snap.docs.forEach(d => { if (pedidoOnlineOculto(d.data())) ocultos.add(d.id); });
        return;
      }
      snap.docChanges().forEach(ch => {
        if (ch.type === "removed") { ocultos.delete(ch.doc.id); return; }
        const p = ch.doc.data();
        if (pedidoOnlineOculto(p)) { ocultos.add(ch.doc.id); return; }
        const pagoAgora = ch.type === "modified" && ocultos.delete(ch.doc.id) && p.status === "pago";
        if (ch.type !== "added" && !pagoAgora) return;
        const cliente = p.cliente?.nome || "Cliente";
        const total = parseFloat(p.total || p.subtotal) || 0;
        mostrarNotif("🛒 Novo pedido!", `${cliente} — R$ ${total.toFixed(2)}`, { type: "pedido_site", pedidoId: ch.doc.id });
      });
    }, () => {});

    // Filamento acabando — notifica uma vez por dia por filamento
    const unsubFil = onSnapshot(collection(db, `users/${uid}/filamentos`), (snap) => {
      const hojeKey = new Date().toISOString().slice(0, 10);
      snap.docs.forEach(d => {
        const f = d.data();
        const peso = parseFloat(f.peso);
        if (!(peso > 0) || f.peso_restante == null) return;
        const pct = parseFloat(f.peso_restante) / peso;
        if (pct >= LIMIAR_BAIXO) return;
        const chave = `notif_fil_${d.id}_${hojeKey}`;
        if (localStorage.getItem(chave)) return;
        localStorage.setItem(chave, "1");
        const nome = f.cor || f.nome || "Filamento";
        mostrarNotif("🧵 Filamento acabando", `${nome} — ${Math.round(pct * 100)}% restante`, { type: "filamento_baixo", filamentoId: d.id });
      });
    }, () => {});

    // Cliente aprovou o orçamento pelo link público — ignora o snapshot
    // inicial (orçamentos já aprovados antes de abrir o app) e notifica só
    // uma vez por orçamento (o doc pode ser reescrito ao gerar o link de novo).
    let primeiraOrc = true;
    const unsubOrc = onSnapshot(collection(db, `users/${uid}/orcamentos_publicos`), (snap) => {
      if (primeiraOrc) { primeiraOrc = false; return; }
      snap.docChanges().forEach(ch => {
        if (ch.type === "removed") return;
        const o = ch.doc.data();
        if (!o.aprovado) return;
        const chave = `notif_orc_aprovado_${ch.doc.id}`;
        if (localStorage.getItem(chave)) return;
        localStorage.setItem(chave, "1");
        mostrarNotif(
          "✅ Orçamento aprovado!",
          `${o.nome || "Cliente"} aprovou o orçamento — R$ ${parseFloat(o.preco || 0).toFixed(2)}`,
          { type: "orcamento_aprovado", orcamentoId: ch.doc.id }
        );
      });
    }, () => {});

    // Mensagem nova de cliente na conversa livre da loja. Ignora o snapshot
    // inicial (conversas que já estavam não lidas) e avisa uma vez por
    // mensagem: o doc também é reescrito pela Cloud Function (controle do
    // e-mail), o que geraria um segundo aviso igual sem o controle por updated_at.
    let primeiraConversas = true;
    const avisadas = new Map();
    const unsubConversas = onSnapshot(collection(db, `users/${uid}/conversas_loja`), (snap) => {
      if (primeiraConversas) {
        primeiraConversas = false;
        snap.docs.forEach(d => avisadas.set(d.id, d.data().updated_at || 0));
        return;
      }
      snap.docChanges().forEach(ch => {
        if (ch.type === "removed") return;
        const c = ch.doc.data();
        if (!c.naoLidoVendedor || c.ultimaMsgAutor !== "cliente") return;
        const marca = c.updated_at || 0;
        if ((avisadas.get(ch.doc.id) || 0) >= marca) return;
        avisadas.set(ch.doc.id, marca);
        mostrarNotif(`💬 ${c.cliente?.nome || "Cliente"}`, c.ultimaMsg || "Nova mensagem", { type: "chat_loja", conversaId: ch.doc.id });
      });
    }, () => {});

    /* 💰 Pagamento recebido: o servidor lança no caixa uma entrada de id fixo
       (mp_… / pg_…) quando confirma, na API do provedor, o pagamento de um
       pedido. Só entradas criadas depois de abrir o app; o vendedor pode
       desligar em Configurações → Pagamentos. (No Android chega por push.) */
    let avisoLigado = true;
    const unsubPreferencia = onSnapshot(doc(db, "users", uid),
      s => { avisoLigado = s.data()?.paymentConfig?.avisoPagamentoRecebido !== false; }, () => {});
    const unsubCaixa = onSnapshot(query(collection(db, fbCollectionPath("caixa")), where("created_at", ">", new Date())), (snap) => {
      snap.docChanges().forEach(ch => {
        if (ch.type !== "added" || !avisoLigado || !/^(mp|pg)_/.test(ch.doc.id)) return;
        const e = ch.doc.data();
        if (e.origem !== "pedido" || localStorage.getItem(`notif_pgto_${ch.doc.id}`)) return;
        localStorage.setItem(`notif_pgto_${ch.doc.id}`, "1");
        const texto = `${fmtMoeda(e.valor)} · ${String(e.descricao || "").replace(/^Pagamento /, "")}`;
        toast(`💰 Pagamento recebido: ${texto}`, "success", 6000);
        mostrarNotif("💰 Pagamento recebido", texto, { type: "pagamento_recebido", pedidoId: e.pedido_id });
      });
    }, () => {});

    return () => { unsubPedidos(); unsubFil(); unsubOrc(); unsubConversas(); unsubPreferencia(); unsubCaixa(); };
  }, [uid]);
}
